import React, { useEffect, useRef, useState } from 'react';
import useLayoutEffect from '@hooks/useLayoutEffect';
import { useRouter } from 'next/router';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { useTranslate } from 'nucleus-text';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import Link from 'next/link';
import { CSSObject } from '@emotion/react';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';
import Icon from '@components/shared/Icon';
import type { NavigationMenuProps } from './NavigationMenu';
import GlobalNav, { NavItem } from './GlobalNav';
import MobileNavMenu from './MobileNavMenu';
import SkipNavigation from './SkipNavigation';
import EventLogo from './EventLogo';
import NavigationFlyouts from './NavigationFlyouts';

export interface NavigationProps extends NavigationMenuProps {
  eventId: string;
  eventTitle: string;
  onLogout: () => void;
  selectedTab: string;
  logoSrc: string;
  /* Prevents the event logo from being wrapped in the home link */
  showLogoLink: boolean;

  /** @deprecated navHeight should not be shared outside of navigation (use CSS or React portal instead) */
  navHeight?: number;
  /** @deprecated navHeight should not be shared outside of navigation (use CSS or React portal instead) */
  setNavHeight?: (height: number) => void;

  myScheduleInfo?: {
    label: string;
    value: string;
  };
  isAttendeeListOrGame?: boolean;
  hasLinks?: boolean;

  pages: NavItem[];

  flyoutControls?: React.ReactNode[];

  unreadMessageCount: number;
  unreadReportCount: number;

  hideNavBar?: boolean;
  hideHeader?: boolean;

  // Feature flags
  viewYourEvents?: boolean;

  isAttendeeProfileEnabled: boolean;
}

const logoPadding = {
  start: 24,
  end: 8
};

const useStyles = ({ hideHeader }): Record<string, CSSObject> => {
  const { font, block } = useTheme();
  const { isMobile, isL, isXL } = useBreakpoints();

  let skipNavLeftOffset;
  if (isL || isXL) {
    skipNavLeftOffset = '40px';
  } else if (isMobile) {
    skipNavLeftOffset = '33px';
  } else {
    skipNavLeftOffset = '45px';
  }

  return {
    desktopContainer: {
      'div[data-layout="col"]': {
        transition: 'none !important'
      },
      display: hideHeader ? 'none' : ''
    },
    tabletContainer: {
      display: hideHeader ? 'none' : '',
      paddingTop: 18,
      paddingBottom: 18
    },
    desktopContent: {
      maxWidth: null,
      margin: 'auto',
      maxHeight: 150,
      minHeight: 80,
      display: 'flex',
      alignItems: 'center',
      flex: '0',
      paddingTop: 18,
      paddingBottom: 18
    },
    skipNavigation: {
      position: 'absolute',
      top: '8px',
      left: skipNavLeftOffset,
      zIndex: 1
    },
    textLink: {
      textDecoration: 'none',
      cursor: 'pointer'
    },
    backNavLink: {
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      fontSize: font.base.size.m,
      color: font.color.base,
      '&:hover': {
        backgroundColor: block.accent
      }
    }
  };
};

function Navigation({
  eventId,
  logoSrc,
  showLogoLink = true,
  eventTitle,
  attendeeName,
  avatarName,
  attendeeEmail,
  attendeeProfilePhotoSrc,
  isAttendeeProfileEnabled = true,
  onLogout,
  navHeight,
  setNavHeight,
  flyoutControls = [],
  pages,
  viewYourEvents,
  unreadMessageCount,
  unreadReportCount,
  viewConnections,
  hideNavBar = false,
  hideHeader = false,
  pendingConnectionsCount,
  hasLinks
}: NavigationProps) {
  const { translate } = useTranslate();
  const { isMobile, isMobileOrTablet, isL, isXL } = useBreakpoints();
  const router = useRouter();

  const styles = useStyles({
    hideHeader
  });
  const navMenuProps: NavigationMenuProps = {
    eventId,
    attendeeName,
    avatarName,
    attendeeEmail,
    attendeeProfilePhotoSrc,
    isAttendeeProfileEnabled,
    onLogout,
    viewYourEvents,
    viewConnections,
    unreadReportCount,
    pendingConnectionsCount
  };
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  // Close menu on navigation
  useEffect(() => {
    const handleRouteChange = () => setShowMobileMenu(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events]);

  const [localNavHeight, setLocalNavHeight] = useState(navHeight);
  const navHeightRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (setNavHeight) {
      setNavHeight?.(navHeightRef.current?.clientHeight);
    } else {
      setLocalNavHeight(navHeightRef.current?.clientHeight);
    }
  });

  const baseLink = `/events/${eventId}`;
  const homeLink = hasLinks ? baseLink : `${baseLink}/info`;
  const logo = (
    <EventLogo
      logoSrc={logoSrc}
      logoLink={showLogoLink ? homeLink : null}
      logoWidth={null}
      testID="page-header-logo-image"
    />
  );

  const showEventHomeLinkResult =
    router.asPath !== homeLink && hasLinks != null && !hasLinks && router.asPath !== baseLink;
  const eventHomeLinkComponent = showEventHomeLinkResult ? (
    <div css={[styles.desktopContent]}>
      <Row>
        <Col flex={{ display: 'flex', alignItems: 'center' }} width="content" padding={logoPadding}>
          <Link
            href={homeLink}
            passHref
            {...injectTestId('page-home-link')}
            css={[styles.textLink, styles.backNavLink]}
            tabIndex={0}
          >
            <Row>
              <Col flex={{ display: 'flex', alignItems: 'center' }} width="content">
                <Icon
                  name="chevron-left"
                  size="lg"
                  color={String(styles.backNavLink.color)}
                  respectThemeSettings={false}
                  aria-hidden
                />
              </Col>
              <Col flex={{ display: 'flex', alignItems: 'center' }} width="content" padding={{ start: 0, end: 20 }}>
                {translate('Navigation-Page-Home')}
              </Col>
            </Row>
          </Link>
        </Col>
      </Row>
    </div>
  ) : null;

  /**
   * TODO:
   * - Create a separate navigation component for Mobile/Tables devices
   * - Rework layout to use CSS instead of Javascript
   */
  return isMobileOrTablet ? (
    <>
      <div css={styles.tabletContainer} {...injectTestId('navigation-mobile')} ref={navHeightRef}>
        {hasLinks && <SkipNavigation style={styles.skipNavigation} />}

        <Row>
          <Col padding={logoPadding} flex={{ display: 'flex', alignItems: 'center' }} width="fill">
            {logo}
          </Col>
          {!isMobile ? (
            <Col padding={{ end: 12 }} flex={{ display: 'flex', alignItems: 'center' }} width="content">
              {!hideNavBar
                ? flyoutControls.map((flyout, index) => (
                    <Col key={index} flex={{ display: 'flex', alignItems: 'center' }} width="content">
                      {flyout}
                    </Col>
                  ))
                : null}
            </Col>
          ) : null}

          <Col
            padding={{ end: 24 }} // if the end padding is changed also update in useNavFlyoutPlacement
            flex={{ display: 'flex', alignItems: 'center' }}
            width="content"
          >
            {!hideNavBar && (
              <GlobalNav
                showMobileMenu={showMobileMenu}
                setShowMobileMenu={setShowMobileMenu}
                globalNav={pages}
                testID="nav"
                unreadMessageCount={unreadMessageCount}
                unreadReportCount={unreadReportCount}
                pendingConnectionsCount={pendingConnectionsCount}
              />
            )}
          </Col>
        </Row>
        {eventHomeLinkComponent}
      </div>
      {eventTitle && eventId && attendeeName && attendeeEmail && (
        <MoodThemeProvider>
          <MobileNavMenu
            showMobileMenu={showMobileMenu}
            navHeight={navHeight ?? localNavHeight}
            eventTitle={eventTitle}
            flyoutControls={flyoutControls}
            pages={pages}
            attendeeName={attendeeName}
            avatarName={avatarName}
            attendeeEmail={attendeeEmail}
            attendeeProfilePhotoSrc={attendeeProfilePhotoSrc}
            onLogout={onLogout}
            unreadReportCount={unreadReportCount}
            pendingConnectionsCount={pendingConnectionsCount}
          />
        </MoodThemeProvider>
      )}
    </>
  ) : (
    <div ref={navHeightRef} {...injectTestId('navigation-desktop')} css={styles.desktopContainer}>
      {hasLinks && <SkipNavigation style={styles.skipNavigation} />}
      <div css={[styles.desktopContent]}>
        <Row>
          <Col flex={{ display: 'flex', alignItems: 'center' }} width="content" padding={logoPadding}>
            {logo}
          </Col>
          <Col flex={{ display: 'flex', alignItems: 'center' }} width="fill">
            {!hideNavBar && (
              <GlobalNav
                showMobileMenu={showMobileMenu}
                setShowMobileMenu={setShowMobileMenu}
                globalNav={!hasLinks ? [] : pages}
                testID="nav"
              />
            )}
          </Col>
          {(isL || isXL) && !hideNavBar ? (
            <NavigationFlyouts flyoutControls={flyoutControls} navMenuProps={navMenuProps} />
          ) : null}
        </Row>
      </div>
      {eventHomeLinkComponent}
    </div>
  );
}

export default Navigation;
